import React, {
  useEffect, useState,
} from "react";
import throttle from "lodash.throttle";
import { CalculatorTemplate } from "../../../../templates/CalculatorTemplate";
import { ICalculatorTabInformation } from "../../../../types/ICalculatorTabInformation";
import { IContentfulRichText } from "../../../../types/IContentfulRichText";

import {
  calculateMonthlyRepaymentAmount,
  calculateTotalBorrowAmount,
} from "../calculatorMethods";
import { formatNumberWithCommas } from "../stringFormatMethods";
import { BorrowForm } from "./forms/borrowForm";
import { CostForm } from "./forms/costForm";

const DEBOUNCE_RESULTS = 1000;

export interface IAffordabilityPanelProps {
  title?: string;
  description: IContentfulRichText;
  disclaimer: IContentfulRichText;
  footerHeading?: string;
  footerText?: string;
  ctaText?: string;
  ctaUrl?: string;
  secondCtaText?: string;
  secondCtaUrl?: string;
  tabInformation?: ICalculatorTabInformation[];
  defaultTab: number;
  mortgageRateOverride: string;
  isAtTopOfPage?: boolean;
  id: string;
}

const updateDisplay = (
  setDisplay: (amount: string) => void,
  amount: number,
  defaultDisplay = "£000,000",
) => setDisplay(amount > 0
  ? `£${formatNumberWithCommas(amount.toString())}`
  : defaultDisplay);

const updateAmountDisplay = async (
  setHousePriceDisplay: (amount: string) => void,
  setBorrowingAmountDisplay: (amount: string) => void,
  setMonthlyPaymentsDisplay: (amount: string) => void,
  setLtv: (amount: string) => void,
  housePrice: number,
  borrowAmount: number,
  monthlyPayments: number,
  isMortgageTermMissing: boolean,
) => {
  updateDisplay(setHousePriceDisplay, housePrice);
  updateDisplay(setBorrowingAmountDisplay, borrowAmount, "");
  if (!isMortgageTermMissing) {
    updateDisplay(
      setMonthlyPaymentsDisplay,
      monthlyPayments,
      "£000",
    );
  }
  const LTV = borrowAmount
    ? `${Math.round((borrowAmount / housePrice) * 100)}%`
    : "00%";
  setLtv(LTV);
};

const throttleAmountDisplay = throttle(updateAmountDisplay, DEBOUNCE_RESULTS, {
  leading: false,
  trailing: true,
});

export const AffordabilityCalculatorPanel: React.FunctionComponent<IAffordabilityPanelProps> = (props) => {
  const {
    title,
    description,
    disclaimer,
    ctaText,
    ctaUrl,
    secondCtaText,
    secondCtaUrl,
    footerHeading,
    footerText,
    defaultTab,
    mortgageRateOverride,
    tabInformation,
    isAtTopOfPage,
    id,
  } = props;

  const [annualSalary, setAnnualSalary] = useState<string>();
  const [partnersSalary, setPartnersSalary] = useState<string>();
  const [totalDeposit, setTotalDeposit] = useState<string>();
  const [totalRegularSpend, setTotalRegularSpend] = useState<string>();
  const [enteredBorrowAmount, setEnteredBorrowAmount] = useState<string>();
  const [mortgageTerm, setMortgageTerm] = useState<string>();
  const [mortgageRate, setMortgageRate] = useState(mortgageRateOverride);
  const [currentTab, setCurrentTab] = useState(defaultTab - 1);

  // Testing this method is not practical. Something to consider
  // the next time we use a fuctional component with hooks
  /* istanbul ignore next */
  const onTabClick = (index: number, amount?: string) => {
    setCurrentTab(index);

    // We only set the borrow amount the first time we go to tab 1
    if (index === 1 && !enteredBorrowAmount) {
      setEnteredBorrowAmount(amount ? amount.replace("£", "") : undefined);
    }
  };

  const monthlyInterestRate = (parseFloat(mortgageRate) || 0) / 100 / 12;

  const isMortgageTermMissing = currentTab === 1 && mortgageTerm === undefined;
  const numberOfMonths = currentTab === 1 && mortgageTerm !== undefined ? parseInt(mortgageTerm, 10) * 12 : 30 * 12;

  const calculatedBorrowAmount = calculateTotalBorrowAmount(
    annualSalary,
    partnersSalary,
    totalRegularSpend,
  );

  const deposit = parseFloat(
    (totalDeposit && totalDeposit.replace(/,/g, "")) || "0",
  );

  const borrowAmount = Math.round(
    currentTab === 0
      ? calculatedBorrowAmount
      : parseFloat(
        (enteredBorrowAmount && enteredBorrowAmount.replace(/,/g, "")) || "0",
      ),
  );
  const housePrice = Math.round(borrowAmount + deposit);

  const monthlyPayments = Math.round(
    calculateMonthlyRepaymentAmount(
      borrowAmount,
      monthlyInterestRate,
      numberOfMonths,
    ),
  );

  const [borrowAmountDisplay, setBorrowAmountDisplay] = useState("");
  const [housePriceDisplay, setHousePriceDisplay] = useState("£000,000");
  const [LTV, setLTV] = useState("00%");
  const [monthlyPaymentsDisplay, setMonthlyPaymentsDisplay] = useState("£000");

  useEffect(() => {
    throttleAmountDisplay(
      setHousePriceDisplay,
      setBorrowAmountDisplay,
      setMonthlyPaymentsDisplay,
      setLTV,
      housePrice,
      borrowAmount,
      monthlyPayments,
      isMortgageTermMissing,
    );
  }, [borrowAmount, housePrice, isMortgageTermMissing, monthlyPayments]);

  // Shared calculatorResults models
  const cta = {
    title: ctaText || "Find a mortgage",
    url: ctaUrl || "#",
  };

  const secondCta = {
    title: secondCtaText,
    url: secondCtaUrl,
  };

  const bottomTitle = "Other fees you may have to pay:";

  const bottom = {
    "Broker fee": "(free with Better.co.uk)",
    "Additional fees": (
      <a
        href="/faq/the-cost-of-buying-a-house"
        target="_blank"
        className="calculator-results__link"
      >
        (learn more)
      </a>
    ),
  };

  const footer = {
    heading: footerHeading || "Your next steps:",
    text:
      footerText
      || "Our mortgage advisors will help you find the right mortgage for your cirmumstances.",
  };

  return (
    <>
      <CalculatorTemplate
        isAtTopOfPage={isAtTopOfPage}
        title={title || "Mortgage Calculator: How Much Can I Borrow?"}
        description={
        description
        || `Our simple mortgage calculator shows how much you can borrow as a UK first-time buyer, 
        to remortgage, or move home. Calculate your monthly mortgage repayments to work out how much mortgage you can afford.`
      }
        disclaimer={disclaimer || ""}
        defaultTab={defaultTab}
        onTabClick={onTabClick}
        id={id}
        tabInformation={
        tabInformation || [
          {
            form: (
              <BorrowForm
                annualSalary={annualSalary}
                partnersSalary={partnersSalary}
                totalDeposit={totalDeposit}
                totalRegularSpend={totalRegularSpend}
                setAnnualSalary={setAnnualSalary}
                setPartnersSalary={setPartnersSalary}
                setTotalDeposit={setTotalDeposit}
                setTotalRegularSpend={setTotalRegularSpend}
              />
            ),
            calculatorResults: {
              bottomTitle,
              bottom,
              cta,
              secondCta,
              footer,
              middle: {
                "Loan to value (LTV):": LTV,
                "Including your deposit, you could afford a house price up to": housePriceDisplay,
              },
              top: {
                result: borrowAmountDisplay,
                title: "You could borrow up to",
              },
            },
            title: "How much can I borrow?",
          },
          {
            form: (
              <CostForm // Mortgage repayment
                calculatedBorrowAmount={calculatedBorrowAmount > 0 ? formatNumberWithCommas(
                  Math.round(calculatedBorrowAmount).toString(),
                ) : ""}
                enteredBorrowAmount={enteredBorrowAmount}
                mortgageTerm={mortgageTerm}
                mortgageRate={mortgageRate}
                setEnteredBorrowAmount={setEnteredBorrowAmount}
                setMortgageTerm={setMortgageTerm}
                setMortgageRate={setMortgageRate}
              />
            ),
            calculatorResults: {
              bottomTitle,
              bottom,
              cta,
              secondCta,
              footer,
              middle: {},
              top: {
                result: monthlyPaymentsDisplay,
                title: "Your monthly mortgage repayment",
              },
            },
            title: "Mortgage repayment calculator",
          },
        ]
      }
      />
    </>
  );
};
